import { Fragment, useEffect, type ReactNode } from 'react';
import { matchPath, useLocation, useNavigate } from 'react-router-dom';
import { useVendor } from '@/contexts/VendorContext';
import { useAdvisoryOrganization } from '@/hooks/useAdvisoryOrganization';
import { AdvisoryContextDisabled, AdvisoryContextMissing } from './AdvisoryPageStates';

import { Button } from '@/components/ui/button';
import type { RuntimeAdvisoriesFlagStatus } from '@/hooks/useRuntimeAdvisoriesFlag';
import { DataStatePanel } from '@/components/ui/data-state-panel';
import { useAdvisoriesShell } from '@/hooks/useAdvisoriesShell';
import { useVendorAdvisoriesShell } from '@/hooks/useVendorAdvisoriesShell';
import { clearPublicToken, recallPublicToken, stashPublicTokens } from '@/lib/publicTokenSession';

/** Resolve historical email links before mounting either portal or its advisory reads. */
export function AdvisoryEmailBoundary({ children }: { children: ReactNode }) {
  const location = useLocation();
  const navigate = useNavigate();
  const match = ['/advisories/:advisoryId', '/msp/advisories/:advisoryId', '/vendor/advisories/:advisoryId']
    .map((path) => matchPath(path, location.pathname)).find(Boolean);
  const token = match ? new URLSearchParams(location.search).get('t') || recallPublicToken('t', location.pathname) : null;
  const advisoryId = match?.params.advisoryId;
  useEffect(() => {
    if (!advisoryId || !token) return;
    const receiptPath = `/advisories/email/${encodeURIComponent(advisoryId)}`;
    // The pre-init scrub has already hidden this token. Move its retained copy without
    // putting it back into Replay-visible history or an unauthenticated returnTo URL.
    stashPublicTokens(receiptPath, new URLSearchParams({ t: token }).toString());
    clearPublicToken('t', location.pathname);
    navigate(receiptPath, { replace: true });
  }, [advisoryId, token, location.pathname, navigate]);
  return advisoryId && token ? <DataStatePanel mode="loading" title="Opening advisory" description="Resolving the companies in your email." /> : <>{children}</>;
}

export function RuntimeAdvisoriesGate({ enabled, loading, status, retry, children, companyName = 'this company' }: { enabled: boolean; loading: boolean; status: RuntimeAdvisoriesFlagStatus; retry: () => void; fallback: string; companyName?: string; children: ReactNode }) {
  if (loading) {
    // Fail closed while the flag loads: no content, and never a redirect mid-load.
    return <DataStatePanel mode="loading" title="Loading" description="Checking access to advisories." data-testid="runtime-advisories-flag-loading" />;
  }
  if (status === 'error') {
    return <DataStatePanel title="Advisories unavailable" description="Could not check access to advisories. Try again."
      action={<Button onClick={retry}>Retry</Button>} data-testid="runtime-advisories-flag-error" />;
  }
  if (!enabled) return <AdvisoryContextDisabled companyName={companyName} />;
  return <>{children}</>;
}

/** ENT-6091: finish any CTA context switch before checking exposure or mounting the page.
 * A context change remounts action dialogs and selections within the scoped route.
 */
export function RuntimeAdvisoriesRoute({ children, fallback = '/dashboard', mspView = false }: { children: ReactNode; fallback?: string; mspView?: boolean }) {
  const context = useAdvisoryOrganization(mspView);
  const shell = useAdvisoriesShell(mspView, !context.loading && !context.error && Boolean(context.contextId));
  if (context.error) return <DataStatePanel title="Advisories unavailable" description={context.error === 'forbidden' ? "You don't have access to this company's advisories." : "Could not open this company's advisories. Try again."} action={context.error === 'unavailable' ? <Button onClick={context.retry}>Retry</Button> : undefined} data-testid="advisory-context-error" />;
  if (!context.loading && !context.contextId) return <AdvisoryContextMissing />;
  return <RuntimeAdvisoriesGate enabled={shell.enabled} loading={context.loading || shell.loading} status={shell.status} retry={shell.retry} fallback={fallback} companyName={mspView ? 'your managed customers' : context.companyName}>
    <Fragment key={`${mspView}:${context.contextId}:${context.userId}`}>{children}</Fragment>
  </RuntimeAdvisoriesGate>;
}

/** ENT-6091: supplier exposure and cache lifetime belong to the open profile. */
export function VendorRuntimeAdvisoriesRoute({ children, fallback = '/vendor' }: { children: ReactNode; fallback?: string }) {
  const { vendorProfile, loading, requestedAdvisoryProfileId } = useVendor();
  const shell = useVendorAdvisoriesShell();
  if (!loading && requestedAdvisoryProfileId && vendorProfile?.id !== requestedAdvisoryProfileId) return <DataStatePanel title="Advisories unavailable" description="You don't have access to this company's advisories." />;
  if (!loading && !vendorProfile?.id) return <AdvisoryContextMissing vendor />;
  return <RuntimeAdvisoriesGate enabled={shell.enabled} loading={loading || shell.loading} status={shell.status} retry={shell.retry} fallback={fallback} companyName={vendorProfile?.listing?.company_name ?? 'this company'}>
    <Fragment key={vendorProfile?.id}>{children}</Fragment>
  </RuntimeAdvisoriesGate>;
}
